// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryCard, { CategoryCardStyles } from "@rpsc/App/Components/Category/CategoryCard";
import FancyScroll, { FancyScrollRefProps } from "@rpsc/App/Components/Common/FancyScroll";
import XngCarousel from "@rpsc/App/Components/XngCarousel";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { isTouchDevice } from "@rpsc/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
    // XNG-Change
    showAsCarousel = "showAsCarousel",
    showParentCategory = "showParentCategory",
    title = "title",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: InjectableCss;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // XNG-Change
    parentLink?: LinkPresentationProps;
    titleContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    links?: InjectableCss;
    subTitle?: TypographyPresentationProps;
    card?: CategoryCardStyles;
    seeLessLink?: LinkPresentationProps;
    seeMoreLink?: LinkPresentationProps;
    scrollableContainer?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    subCategoryItem: {
        width: [12, 12, 4, 4, 2],
        css: css`
            padding-bottom: 30px;
        `,
    },
    innerContainer: {
        gap: 0,
    },
    subCategoryImageItem: {
        width: [2, 2, 12, 12, 12],
        css: css`
            width: 100%;
            padding: 0 8px 8px 8px;
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [10, 10, 12, 12, 12],
        css: css`
            padding-bottom: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                ])}
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                text-align: center;
                word-wrap: break-word;
            `,
        },
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({
    category,
    fields: { showImages, showAsCarousel, title, showParentCategory },
}: Props) => {
    // XNG-Changes
    const [expanded, setExpanded] = React.useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    const { isTabletScreen, productListCatalogPage } = useAppSelector(state => {
        const location = getLocation(state);
        const selectedCategoryPath = getSelectedCategoryPath(state);
        const pathnameLowerCase = location.pathname.toLowerCase();
        const path =
            selectedCategoryPath ||
            (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
                ? ""
                : location.pathname);
        return {
            isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
            productListCatalogPage: getCatalogPageStateByPath(state, path).value,
        };
    });

    React.useEffect(() => {
        fancyScrollRef.current && fancyScrollRef.current?.updateScrollable();
    }, [expanded]);

    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }
    const breadCrumbs = productListCatalogPage?.breadCrumbs?.slice(1);

    const parentLink = breadCrumbs?.[breadCrumbs.length - 2];

    // XNG-Changes
    const limitedSubCategoryIds = expanded ? category.subCategoryIds : category.subCategoryIds?.slice(0, 5);

    return (
        <>
            {showAsCarousel ? (
                <StyledWrapper>
                    <XngCarousel
                        scrollbar={isTabletScreen}
                        spaceBetween={40}
                        rewind={!isTabletScreen}
                        navigation={!isTabletScreen}
                        draggable={isTouchDevice()}
                    >
                        {category.subCategoryIds.map(subCategoryId => (
                            <StyledWrapper key={subCategoryId}>
                                <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                            </StyledWrapper>
                        ))}
                    </XngCarousel>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container}>
                    {title && showParentCategory && !!parentLink?.url && (
                        <StyledWrapper {...styles.titleContainer}>
                            {title && <Typography {...styles.title}>{title}</Typography>}
                            {showParentCategory && !!parentLink?.url && (
                                <Link
                                    href={parentLink.url}
                                    {...styles.parentLink}
                                    data-test-selector={`categoryDetailsParentCategoriesLink_${category.id}`}
                                    aria-label={parentLink.text}
                                >
                                    {parentLink.text}
                                </Link>
                            )}
                        </StyledWrapper>
                    )}
                    <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef} shadowHeight="8px">
                        <StyledWrapper ref={parentBoxRef} {...styles.scrollableContainer}>
                            <Typography {...styles.subTitle}>
                                {translate("{0} Sub Categories", category.shortDescription)}
                            </Typography>
                            {limitedSubCategoryIds.map(subCategoryId => (
                                <SubCategoryLink
                                    key={subCategoryId}
                                    categoryId={subCategoryId}
                                    showImages={showImages}
                                    showLinkOnly
                                />
                            ))}
                            {/* XNG-Changes */}
                            {category.subCategoryIds.length > 5 &&
                                (expanded ? (
                                    <Link
                                        aria-label={translate("Show Less")}
                                        {...styles.seeLessLink}
                                        onClick={() => setExpanded(false)}
                                    >
                                        {translate("Show Less")}
                                    </Link>
                                ) : (
                                    <Link
                                        aria-label={translate("Show More")}
                                        {...styles.seeMoreLink}
                                        onClick={() => setExpanded(true)}
                                    >
                                        {translate("Show More")}
                                    </Link>
                                ))}
                        </StyledWrapper>
                    </FancyScroll>
                </StyledWrapper>
            )}
        </>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
    showLinkOnly,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean; showLinkOnly?: boolean }) => {
    if (!category) {
        return null;
    }
    // XNG-Change: using category card
    return showLinkOnly ? (
        <Link
            href={category.path}
            {...styles.subCategoryNameLink}
            data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
            aria-label={category.shortDescription}
        >
            {category.shortDescription}
        </Link>
    ) : (
        <CategoryCard category={category} showImages={showImages} extendedStyles={styles.card} />
    );
    /* <GridContainer {...styles.innerContainer}>

         {showImages && (
                <GridItem {...styles.subCategoryImageItem}>
                    {category.smallImagePath && (
                        <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                            <LazyImage
                                src={category.smallImagePath}
                                altText={category.imageAltText}
                                {...styles.subCategoryImage}
                            />
                        </Clickable>
                    )}
                </GridItem>
            )}
            <GridItem {...styles.subCategoryNameLinkItem}>
                <Link
                    href={category.path}
                    {...styles.subCategoryNameLink}
                    data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                    aria-label={category.shortDescription}
                >
                    {category.shortDescription}
                </Link>
            </GridItem>
        </GridContainer>
        */
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showAsCarousel,
                displayName: "Show As Carousel",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 3,
            },
            {
                name: fields.showParentCategory,
                displayName: "Show Parent Category",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
